import type { Firestore, Query } from 'firebase/firestore'
import {
  and,
  collection,
  collectionGroup,
  query,
  where,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import {
  convertDocumentSnapshotToModel,
  modelListStream,
} from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { RoomStateRubricResultDetail } from '../../models/RoomStateRubricResultDetail'
import type { FirestoreRoomStateRubricResultDetail } from './schema'
import { schema } from './schema'
import { type RubricType } from '../PromptRubricType'
import { getDocsWithError } from '../../firestore-mobx/fetch'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreRoomStateRubricResultDetail> =
  {
    toFirestore: () => {
      throw new Error('Not implemented')
    },
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return schema.parse(data)
    },
  }

function getColGroupRef(
  firestore: Firestore
): Query<FirestoreRoomStateRubricResultDetail> {
  return collectionGroup(firestore, 'rubric_result_detail').withConverter(
    converter
  )
}

export const getRoomStateRubricResultDetailsForRoom = (
  repository: FirebaseRepository,
  { roomId }: { roomId: string }
) => {
  const q = query(
    getColGroupRef(repository.firestore),
    and(where('roomId', '==', roomId))
  )

  return modelListStream(repository, q, RoomStateRubricResultDetail)
}

export const getRoomStateRubricResultDetailsForInstructor = (
  repository: FirebaseRepository,
  {
    roomId,
    sectionId,
    assignmentId,
  }: { roomId: string; sectionId: string; assignmentId: string }
) => {
  const q = query(
    getColGroupRef(repository.firestore),
    and(
      where('assignmentId', '==', assignmentId),
      where('sectionId', '==', sectionId),
      where('roomId', '==', roomId)
    )
  )

  return modelListStream(repository, q, RoomStateRubricResultDetail)
}

export const getRoomStateRubricResultDetailsForAssignment = (
  repository: FirebaseRepository,
  { assignmentId, sectionId }: { assignmentId: string; sectionId: string }
) => {
  const q = query(
    getColGroupRef(repository.firestore),
    and(
      where('assignmentId', '==', assignmentId),
      where('sectionId', '==', sectionId)
    )
  )

  return modelListStream(repository, q, RoomStateRubricResultDetail)
}

/**
 * prompt/rubric_prompt/type/{rubricType}/prompt_version/{promptVersionId}/test/{testId}/result/{resultId}/user_ai_result/{userId}/ai_result/fake-rubric
 */
export const fetchRubricPromptTestRubricResultDetailsForUser = async (
  repository: FirebaseRepository,
  {
    rubricType,
    promptVersionId,
    testId,
    resultId,
    userId,
  }: {
    rubricType: RubricType
    promptVersionId: string
    testId: string
    resultId: string
    userId: string
  }
) => {
  const ref = collection(
    repository.firestore,
    'prompt',
    'rubric_prompt',
    'type',
    rubricType.toString(),
    'prompt_version',
    promptVersionId,
    'test',
    testId,
    'result',
    resultId,
    'user_ai_result',
    userId,
    'ai_result',
    'fake-rubric',
    'ai_result_detail'
  ).withConverter(converter)

  const res = await getDocsWithError(
    ref,
    'FetchRubricPromptTestRubricResultDetailsForUserError'
  )

  return res.docs.map((doc) =>
    convertDocumentSnapshotToModel(repository, doc, RoomStateRubricResultDetail)
  )
}
